import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Package, CheckCircle2, Wallet, Landmark, Boxes, Scale, ScrollText, Download, Plus } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Field, Input, Select, Textarea } from '@/components/ui/Field';
import { StatCard } from '@/components/ui/StatCard';
import { EmptyState, Spinner } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/ToastContext';
import { supabase } from '@/lib/supabaseClient';
import { exportToCsv } from '@/lib/exportCsv';

// How a partner paid; stored on core_ar_payments.method and shown in the
// Sales & Payments Summary. Reference = TrxID / cheque no. / bank ref.
const PAYMENT_METHODS: { value: string; label: string; refHint: string }[] = [
  { value: 'cash', label: 'Cash', refHint: 'Optional — e.g. received at office' },
  { value: 'bkash', label: 'bKash', refHint: 'bKash TrxID' },
  { value: 'nagad', label: 'Nagad', refHint: 'Nagad TrxID' },
  { value: 'rocket', label: 'Rocket', refHint: 'Rocket TrxID' },
  { value: 'bank', label: 'Bank Transfer', refHint: 'Bank name + transaction ref' },
  { value: 'cheque', label: 'Cheque', refHint: 'Cheque no. + bank' },
  { value: 'card', label: 'Card', refHint: 'Card slip / approval no.' },
];

// A partner's consignment account: what they were given, what they have
// sold (Report Sale), what they have paid (payments + order-time advances),
// what is owed now on the sold units, and what is owed in total — plus a
// dated statement with running balances. Totals come from
// core_partner_account_v, rows from core_partner_ledger_events_v
// (thekseoul-admin-hub migration 20261008150000).

interface Account {
  given_bdt: number;
  given_qty: number;
  sold_bdt: number;
  sold_qty: number;
  returned_bdt: number;
  returned_qty: number;
  paid_bdt: number;
  total_due_bdt: number;
  ar_due_bdt: number;
  ar_difference_bdt: number;
  unsold_qty: number;
  unsold_bdt: number;
  due_now_bdt: number;
}

type EventKind = 'shipment' | 'advance' | 'sale' | 'offer' | 'return' | 'payment';

interface LedgerEvent {
  event_at: string;
  kind: EventKind;
  ref: string | null;
  qty: number | null;
  amount_bdt: number;
  note: string | null;
}

interface StatementRow extends LedgerEvent {
  dueNow: number;
  totalDue: number;
}

const KIND_LABEL: Record<EventKind, string> = {
  shipment: 'Goods given',
  advance: 'Advance paid',
  sale: 'Sale reported',
  offer: 'Offer discount',
  return: 'Returned',
  payment: 'Payment',
};

// How each event moves the two balances. Goods given only raise the total
// owed; a sale makes that much owed now; money in and returns lower them.
const EFFECT: Record<EventKind, { dueNow: number; totalDue: number }> = {
  shipment: { dueNow: 0, totalDue: 1 },
  advance: { dueNow: -1, totalDue: -1 },
  sale: { dueNow: 1, totalDue: 0 },
  offer: { dueNow: -1, totalDue: -1 },
  return: { dueNow: 0, totalDue: -1 },
  payment: { dueNow: -1, totalDue: -1 },
};

const num = (v: unknown) => Number(v) || 0;
const money = (v: number) => `৳${Math.round(v * 100) / 100 === Math.round(v) ? Math.round(v).toLocaleString() : v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtDate = (d: string) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
const todayIso = () => new Date().toISOString().slice(0, 10);

export function PartnerAccountPanel({ partnerId, partnerName, reloadKey, onChanged }: { partnerId: string; partnerName: string; reloadKey?: number; onChanged?: () => void }) {
  const { push } = useToast();
  const [account, setAccount] = useState<Account | null>(null);
  const [events, setEvents] = useState<LedgerEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ amount: '', date: todayIso(), method: '', reference: '', note: '' });
  const [busy, setBusy] = useState(false);
  const [excelBusy, setExcelBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [accRes, evRes] = await Promise.all([
      supabase.from('core_partner_account_v').select('*').eq('partner_id', partnerId).maybeSingle(),
      supabase.from('core_partner_ledger_events_v').select('event_at, kind, ref, qty, amount_bdt, note').eq('partner_id', partnerId).order('event_at', { ascending: true }),
    ]);
    if (accRes.error) push('error', accRes.error.message);
    const a = accRes.data as Record<string, unknown> | null;
    setAccount(
      a
        ? {
            given_bdt: num(a.given_bdt),
            given_qty: num(a.given_qty),
            sold_bdt: num(a.sold_bdt),
            sold_qty: num(a.sold_qty),
            returned_bdt: num(a.returned_bdt),
            returned_qty: num(a.returned_qty),
            paid_bdt: num(a.paid_bdt),
            total_due_bdt: num(a.total_due_bdt),
            ar_due_bdt: num(a.ar_due_bdt),
            ar_difference_bdt: num(a.ar_difference_bdt),
            unsold_qty: num(a.unsold_qty),
            unsold_bdt: num(a.unsold_bdt),
            due_now_bdt: num(a.due_now_bdt),
          }
        : null,
    );
    setEvents(((evRes.data as LedgerEvent[]) ?? []).map((e) => ({ ...e, amount_bdt: num(e.amount_bdt) })));
    setLoading(false);
  }, [partnerId, push]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  // Running balances over the whole history, oldest first — the last row
  // always lands on the account totals above.
  const statement = useMemo<StatementRow[]>(() => {
    let dueNow = 0;
    let totalDue = 0;
    return events.map((e) => {
      dueNow += EFFECT[e.kind].dueNow * e.amount_bdt;
      totalDue += EFFECT[e.kind].totalDue * e.amount_bdt;
      return { ...e, dueNow, totalDue };
    });
  }, [events]);

  const visible = useMemo(() => {
    const fromT = from ? new Date(from).getTime() : -Infinity;
    const toT = to ? new Date(to).getTime() + 24 * 60 * 60 * 1000 - 1 : Infinity;
    return statement.filter((r) => {
      const t = new Date(r.event_at).getTime();
      return t >= fromT && t <= toT;
    });
  }, [statement, from, to]);

  // Balance carried into the first visible row when a From date hides older ones.
  const broughtForward = useMemo(() => {
    if (!from || visible.length === 0) return null;
    const idx = statement.indexOf(visible[0]);
    return idx > 0 ? statement[idx - 1] : null;
  }, [from, statement, visible]);

  const openPayment = () => {
    const suggested = account && account.due_now_bdt > 0 ? String(Math.round(account.due_now_bdt * 100) / 100) : '';
    setPayForm({ amount: suggested, date: todayIso(), method: '', reference: '', note: '' });
    setPayOpen(true);
  };

  const payAmount = Number(payForm.amount) || 0;
  const exceedsDueNow = !!account && payAmount > account.due_now_bdt + 0.01;

  const handlePay = async (e: FormEvent) => {
    e.preventDefault();
    if (payAmount <= 0) {
      push('error', 'Enter the amount received');
      return;
    }
    if (!payForm.method) {
      push('error', 'Choose how the partner paid');
      return;
    }
    setBusy(true);
    const { error } = await supabase.rpc('core_record_partner_payment', {
      p_partner_id: partnerId,
      p_amount_bdt: payAmount,
      p_note: payForm.note.trim() || null,
      p_method: payForm.method,
      p_reference: payForm.reference.trim() || null,
      // Noon local time, so the chosen calendar day never shifts across a timezone boundary.
      p_paid_at: new Date(`${payForm.date}T12:00:00`).toISOString(),
    });
    setBusy(false);
    if (error) {
      push('error', error.message);
      return;
    }
    push('success', 'Payment recorded');
    setPayOpen(false);
    load();
    onChanged?.();
  };

  // CSV rather than xlsx: rxk doesn't bundle exceljs (same as its Stock Overview export).
  const downloadExcel = () => {
    setExcelBusy(true);
    try {
      const headers = ['Date', 'Entry', 'Reference', 'Pcs', 'Amount', 'Due now', 'Total due', 'Note'];
      const rows: (string | number)[][] = [];
      if (broughtForward) rows.push(['', 'Brought forward', '', '', '', broughtForward.dueNow, broughtForward.totalDue, '']);
      for (const r of visible) rows.push([fmtDate(r.event_at), KIND_LABEL[r.kind], r.ref ?? '', r.qty ?? '', r.amount_bdt, r.dueNow, r.totalDue, r.note ?? '']);
      exportToCsv(`${partnerName.replace(/[^\w\- ]+/g, '').trim() || 'partner'} statement ${todayIso()}.csv`, headers, rows);
    } finally {
      setExcelBusy(false);
    }
  };

  if (loading && !account) return <Spinner />;

  const a = account ?? { given_bdt: 0, given_qty: 0, sold_bdt: 0, sold_qty: 0, returned_bdt: 0, returned_qty: 0, paid_bdt: 0, total_due_bdt: 0, ar_due_bdt: 0, ar_difference_bdt: 0, unsold_qty: 0, unsold_bdt: 0, due_now_bdt: 0 };
  const sub = (text: string) => <p className="text-[11px] text-ink-faint">{text}</p>;

  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        <StatCard label="Goods Given" value={money(a.given_bdt)} icon={<Package className="w-4 h-4" />} accent="primary" footer={sub(`${a.given_qty} pcs${a.returned_qty ? ` · ${a.returned_qty} returned` : ''}`)} />
        <StatCard label="Sold" value={money(a.sold_bdt)} icon={<CheckCircle2 className="w-4 h-4" />} accent="success" footer={sub(`${a.sold_qty} pcs reported sold`)} />
        <StatCard label="Paid" value={money(a.paid_bdt)} icon={<Wallet className="w-4 h-4" />} accent="info" footer={sub('Payments + order advances')} />
        <StatCard
          label={a.due_now_bdt < 0 ? 'Paid In Advance' : 'Due Now (on sold)'}
          value={money(Math.abs(a.due_now_bdt))}
          icon={<Scale className="w-4 h-4" />}
          accent={a.due_now_bdt > 0 ? 'error' : 'success'}
          footer={sub(a.due_now_bdt < 0 ? 'Paid ahead of sales' : 'Sold value minus paid')}
        />
        <StatCard label="Unsold Stock" value={money(a.unsold_bdt)} icon={<Boxes className="w-4 h-4" />} accent="primary" footer={sub(`${a.unsold_qty} pcs still with partner`)} />
        <StatCard
          label={a.total_due_bdt < 0 ? 'Credit Balance' : 'Total Due'}
          value={money(Math.abs(a.total_due_bdt))}
          icon={<Landmark className="w-4 h-4" />}
          accent={a.total_due_bdt < 0 ? 'success' : 'warning'}
          footer={sub(a.total_due_bdt < 0 ? 'Partner paid/returned more than given' : 'Given − returned − paid')}
        />
      </div>

      {Math.abs(a.ar_difference_bdt) >= 1 && (
        <div className="rounded-xl border border-warning-border bg-warning-bg px-4 py-3 text-[12px] text-ink print:hidden">
          <p className="font-semibold">Order ledger doesn't match this account</p>
          <p className="text-ink-faint mt-0.5">
            The per-order AR ledger below still shows {money(a.ar_due_bdt)} owed — {money(Math.abs(a.ar_difference_bdt))} {a.ar_difference_bdt < 0 ? 'more' : 'less'} than
            the {money(a.total_due_bdt)} worked out from deliveries, returns and payments. Usually a return recorded before 6 Oct 2026, when returns
            didn't yet reduce the ledger.
          </p>
        </div>
      )}

      <Card>
        <CardHeader
          title="Account Statement"
          subtitle="Every delivery, sale, return and payment by date, with running balances"
          icon={<ScrollText className="w-4 h-4" />}
          action={
            <div className="flex items-center gap-2 print:hidden">
              <Button size="sm" variant="secondary" onClick={downloadExcel} loading={excelBusy} disabled={visible.length === 0}>
                <Download className="w-3.5 h-3.5" /> CSV
              </Button>
              <Button size="sm" onClick={openPayment} disabled={a.ar_due_bdt <= 0}>
                <Plus className="w-3.5 h-3.5" /> Record Payment
              </Button>
            </div>
          }
        />
        <CardBody className="space-y-3">
          <div className="flex flex-wrap items-end gap-3 print:hidden">
            <Field label="From">
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </Field>
            <Field label="To">
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </Field>
            {(from || to) && (
              <Button size="sm" variant="secondary" onClick={() => { setFrom(''); setTo(''); }}>
                Clear
              </Button>
            )}
          </div>

          {visible.length === 0 ? (
            <EmptyState title="No entries in this period" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="text-ink-faint text-[10.5px] uppercase tracking-wide">
                    <th className="text-left py-1.5 px-2">Date</th>
                    <th className="text-left py-1.5 px-2">Entry</th>
                    <th className="text-right py-1.5 px-2">Pcs</th>
                    <th className="text-right py-1.5 px-2">Amount</th>
                    <th className="text-right py-1.5 px-2">Due Now</th>
                    <th className="text-right py-1.5 px-2">Total Due</th>
                  </tr>
                </thead>
                <tbody style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {broughtForward && (
                    <tr className="border-t border-border-subtle bg-surface-muted/60">
                      <td className="py-2 px-2 text-ink-faint" colSpan={4}>Brought forward</td>
                      <td className="py-2 px-2 text-right font-semibold">{money(broughtForward.dueNow)}</td>
                      <td className="py-2 px-2 text-right font-semibold">{money(broughtForward.totalDue)}</td>
                    </tr>
                  )}
                  {visible.map((r, i) => {
                    const moneyIn = r.kind === 'payment' || r.kind === 'advance' || r.kind === 'offer';
                    return (
                      <tr key={`${r.kind}-${r.event_at}-${i}`} className="border-t border-border-subtle hover:bg-surface-muted">
                        <td className="py-2 px-2 text-ink-faint whitespace-nowrap">{fmtDate(r.event_at)}</td>
                        <td className="py-2 px-2">
                          <p className="font-semibold text-ink">
                            {KIND_LABEL[r.kind]}
                            {r.ref && <span className="font-normal text-ink-faint"> · {r.ref}</span>}
                          </p>
                          {r.note && <p className="text-[11px] text-ink-faint truncate max-w-[22rem]">{r.note}</p>}
                        </td>
                        <td className="py-2 px-2 text-right text-ink-faint">{r.qty ?? ''}</td>
                        <td className={`py-2 px-2 text-right font-semibold ${moneyIn ? 'text-success' : 'text-ink'}`}>
                          {moneyIn || r.kind === 'return' ? '−' : '+'}
                          {money(r.amount_bdt)}
                        </td>
                        <td className={`py-2 px-2 text-right ${r.dueNow > 0 ? 'text-error font-semibold' : 'text-ink-faint'}`}>{money(r.dueNow)}</td>
                        <td className="py-2 px-2 text-right font-bold text-ink">{money(r.totalDue)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <Modal open={payOpen} onClose={() => setPayOpen(false)} title="Record Payment" subtitle={partnerName} icon={<Wallet className="w-4 h-4" />} busy={busy}>
        <form onSubmit={handlePay} className="space-y-3">
          <div className="grid grid-cols-2 gap-2 text-[12px]">
            <div className="rounded-lg bg-surface-muted px-3 py-2">
              <p className="text-ink-faint">Due now (on sold)</p>
              <p className="font-bold text-ink">{money(Math.max(0, a.due_now_bdt))}</p>
            </div>
            <div className="rounded-lg bg-surface-muted px-3 py-2">
              <p className="text-ink-faint">Total due</p>
              <p className="font-bold text-ink">{money(Math.max(0, a.total_due_bdt))}</p>
            </div>
          </div>
          <Field label="Amount received (৳)">
            <Input type="number" min={0} step="0.01" max={a.ar_due_bdt} value={payForm.amount} onChange={(e) => setPayForm((f) => ({ ...f, amount: e.target.value }))} required />
          </Field>
          {exceedsDueNow && payAmount <= a.ar_due_bdt + 0.01 && (
            <p className="text-[11.5px] text-info">More than what's due on sold units — the extra is kept as an advance against unsold stock.</p>
          )}
          <Field label="Date received">
            <Input type="date" value={payForm.date} max={todayIso()} onChange={(e) => setPayForm((f) => ({ ...f, date: e.target.value }))} required />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Method">
              <Select value={payForm.method} onChange={(e) => setPayForm((f) => ({ ...f, method: e.target.value }))} required>
                <option value="">Choose…</option>
                {PAYMENT_METHODS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Reference">
              <Input
                value={payForm.reference}
                onChange={(e) => setPayForm((f) => ({ ...f, reference: e.target.value }))}
                placeholder={PAYMENT_METHODS.find((m) => m.value === payForm.method)?.refHint ?? 'TrxID / cheque no.'}
              />
            </Field>
          </div>
          <Field label="Note">
            <Textarea rows={2} value={payForm.note} onChange={(e) => setPayForm((f) => ({ ...f, note: e.target.value }))} placeholder="Optional" />
          </Field>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="secondary" onClick={() => setPayOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" loading={busy}>
              Save Payment
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
